import { forwardRef } from "react"
import { useLingui } from "@lingui/react/macro"
import { SearchIcon, ChevronLeftIcon } from "@/lib/inbox-icons"

// Top input row for the command palette (CON-183).
//
// Two shapes from one component, picked by `variant`:
//   - `modal` (desktop / tablet): left magnifier, input,
//     right `Esc` key-cap pill.
//   - `fullscreen` (mobile): left back chevron (closes), the
//     input, and a clear ✕ that appears once there's a query.
//
// Placeholders match the Paper artboards. They mention "type >
// for commands" — command mode itself is the deferred CON-184
// ticket, so `>` doesn't act on anything yet, but the hint is
// part of the designed cold-start copy.

interface SearchInputProps {
  value: string
  onChange: (value: string) => void
  onClose: () => void
  variant: "modal" | "fullscreen"
  placeholder?: string
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  function SearchInput(
    { value, onChange, onClose, variant, placeholder },
    ref,
  ) {
    const { t } = useLingui()
    const resolvedPlaceholder =
      placeholder ??
      (variant === "fullscreen"
        ? t`Search or type > for commands`
        : t`Search anything, or type > for commands`)
    return (
      <div className="flex items-center gap-2.5 px-4 py-3.5">
        {variant === "fullscreen" ? (
          <button
            type="button"
            onClick={onClose}
            aria-label={t`Close search`}
            className="-ml-1 flex size-7 shrink-0 items-center justify-center rounded-full hover:bg-[#F0EDE6]"
          >
            <ChevronLeftIcon size={16} stroke="#1E1B17" />
          </button>
        ) : (
          <span className="shrink-0">
            <SearchIcon size={18} />
          </span>
        )}

        <input
          ref={ref}
          type="search"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={resolvedPlaceholder}
          aria-label={t`Search`}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          data-testid="command-palette-input"
          // Hide the native search clear/decoration so our own
          // ✕ is the only clear affordance on mobile.
          className="min-w-0 grow bg-transparent text-[15px] leading-5 text-[#1E1B17] outline-none placeholder:text-[#9A938A] [&::-webkit-search-cancel-button]:appearance-none"
        />

        {variant === "modal" ? (
          <kbd className="shrink-0 rounded-md border border-[#E5DFD3] bg-[#FAF7F1] px-1.5 py-0.5 text-[10px] leading-3 font-medium text-[#9A938A]">
            Esc
          </kbd>
        ) : value.length > 0 ? (
          <button
            type="button"
            onClick={() => onChange("")}
            aria-label={t`Clear search`}
            data-testid="command-palette-clear"
            className="flex size-7 shrink-0 items-center justify-center rounded-full hover:bg-[#F0EDE6]"
          >
            <ClearIcon />
          </button>
        ) : null}
      </div>
    )
  },
)

function ClearIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M4 4L12 12M12 4L4 12"
        stroke="#7A746B"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  )
}
